import React, { useState } from 'react';
import { UserPlus, Trash2, Users as UsersIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from '../../components/ui/sonner';
import * as api from '../../services/api';

const BeneficiariesPage = ({ client, refreshClient }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newBeneficiary, setNewBeneficiary] = useState({
    name: '',
    iban: '',
    bic: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const beneficiaries = client.beneficiaries || [];

  const handleAddBeneficiary = async () => {
    if (!newBeneficiary.name || !newBeneficiary.iban) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setIsProcessing(true);
      await api.addBeneficiary(client.id, newBeneficiary);
      toast.success('Bénéficiaire ajouté avec succès');
      setNewBeneficiary({ name: '', iban: '', bic: '' });
      setShowAddForm(false);
      await refreshClient();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du bénéficiaire');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteBeneficiary = async (beneficiaryId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce bénéficiaire ?')) {
      return;
    }

    try {
      setIsProcessing(true);
      await api.deleteBeneficiary(client.id, beneficiaryId);
      toast.success('Bénéficiaire supprimé');
      await refreshClient();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Mes bénéficiaires</CardTitle>
            <Button
              onClick={() => setShowAddForm(!showAddForm)}
              style={{ backgroundColor: '#82C43C' }}
              size="sm"
            >
              <UserPlus size={16} className="mr-2" />
              Ajouter un bénéficiaire
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {showAddForm && (
            <div className="mb-6 p-4 border rounded-lg bg-gray-50">
              <h3 className="font-medium mb-4">Nouveau bénéficiaire</h3>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="name">Nom du bénéficiaire *</Label>
                  <Input
                    id="name"
                    value={newBeneficiary.name}
                    onChange={(e) => setNewBeneficiary({ ...newBeneficiary, name: e.target.value })}
                    placeholder="Ex: Jean Dupont"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="iban">IBAN *</Label>
                  <Input
                    id="iban"
                    value={newBeneficiary.iban}
                    onChange={(e) => setNewBeneficiary({ ...newBeneficiary, iban: e.target.value })}
                    placeholder="FR76 1234 5678 9012 3456 7890 123"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="bic">BIC (optionnel)</Label>
                  <Input
                    id="bic"
                    value={newBeneficiary.bic}
                    onChange={(e) => setNewBeneficiary({ ...newBeneficiary, bic: e.target.value })}
                    placeholder="FTNOFRP1XXX"
                    className="mt-1"
                  />
                </div>
                <div className="flex space-x-2 pt-2">
                  <Button
                    onClick={handleAddBeneficiary}
                    disabled={isProcessing}
                    style={{ backgroundColor: '#82C43C' }}
                  >
                    {isProcessing ? 'Ajout...' : 'Ajouter'}
                  </Button>
                  <Button
                    onClick={() => setShowAddForm(false)}
                    variant="outline"
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            </div>
          )}

          {beneficiaries.length === 0 ? (
            <div className="text-center py-12">
              <UsersIcon className="mx-auto text-gray-400 mb-3" size={48} />
              <p className="text-gray-600 mb-4">Aucun bénéficiaire enregistré</p>
              <Button
                onClick={() => setShowAddForm(true)}
                variant="outline"
              >
                Ajouter votre premier bénéficiaire
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {beneficiaries.map((beneficiary) => (
                <div
                  key={beneficiary.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium text-lg">{beneficiary.name}</p>
                      <p className="text-sm text-gray-600 font-mono mt-1">{beneficiary.iban}</p>
                      {beneficiary.bic && (
                        <p className="text-sm text-gray-500 font-mono">BIC: {beneficiary.bic}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-2">Ajouté le {beneficiary.addedDate}</p>
                    </div>
                    <Button
                      onClick={() => handleDeleteBeneficiary(beneficiary.id)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      disabled={isProcessing}
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-800">
              <strong>ℹ️ À propos des bénéficiaires :</strong><br />
              Vous pouvez enregistrer les coordonnées bancaires de vos bénéficiaires pour faciliter 
              vos virements futurs. Ces informations sont sécurisées et uniquement accessibles par vous.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BeneficiariesPage;